import { apiUrl, UnauthorizedError, viewAsHeaders } from "./api";
import type { AnswerLink, CreatedServiceKey, InboxQuestion, ProductionRun, ServiceKey } from "../../../packages/contracts/src/production";

/** An error from the API, with its HTTP status and error code. */
export class ProductionApiError extends Error {
  constructor(message: string, readonly status: number, readonly code?: string) { super(message); this.name = "ProductionApiError"; }
}

async function read<T>(response: Response): Promise<T> {
  const body = await response.json().catch(() => ({})) as { message?: string; code?: string };
  if (!response.ok) throw new ProductionApiError(body.message || "The request failed.", response.status, body.code);
  return body as T;
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(apiUrl(path), { ...init, credentials: "include",
    headers: { ...init?.headers, ...viewAsHeaders(), "content-type": "application/json", "x-method-visibility": document.visibilityState } });
  if (response.status === 401) { window.dispatchEvent(new Event("workflow:unauthorized")); throw new UnauthorizedError(); }
  return read<T>(response);
}

/** A public call: no session and no credentials. It does not send the user to sign in. */
async function publicCall<T>(path: string, init?: RequestInit): Promise<T> {
  return read<T>(await fetch(apiUrl(path), { ...init, credentials: "omit", headers: { ...init?.headers, "content-type": "application/json" } }));
}

export const productionApi = {
  async cancel(id: string): Promise<ProductionRun> { return (await call<{ run: ProductionRun }>(`/api/production/runs/${encodeURIComponent(id)}/cancel`, { method: "POST", body: "{}" })).run; },
  async questions(): Promise<InboxQuestion[]> { return (await call<{ questions: InboxQuestion[] }>("/api/production/questions")).questions; },
  async answer(runId: string, answer: Record<string, unknown>): Promise<ProductionRun> {
    return (await call<{ run: ProductionRun }>(`/api/production/runs/${encodeURIComponent(runId)}/answer`, { method: "POST", body: JSON.stringify({ answer }) })).run;
  },
  async link(token: string): Promise<AnswerLink> { return publicCall<AnswerLink>(`/public/answers/${encodeURIComponent(token)}`); },
  async answerLink(token: string, answer: Record<string, unknown>): Promise<{ status: "answered" }> {
    return publicCall<{ status: "answered" }>(`/public/answers/${encodeURIComponent(token)}`, { method: "POST", body: JSON.stringify({ answer }) });
  },
  async keys(): Promise<ServiceKey[]> { return (await call<{ keys: ServiceKey[] }>("/api/service-keys")).keys; },
  async createKey(name: string): Promise<CreatedServiceKey> { return call<CreatedServiceKey>("/api/service-keys", { method: "POST", body: JSON.stringify({ name }) }); },
  async revokeKey(id: string): Promise<void> { await call(`/api/service-keys/${encodeURIComponent(id)}/revoke`, { method: "POST", body: "{}" }); },
};
